import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { founderDashboardAPI } from '../../../../services/api';
import {
  Modal,
  PageHeader,
  PageState,
  ReasonList,
  StaleNotice,
  StatusBadge,
} from './OutreachShared';
import {
  focusRing,
  formatDateTime,
  humanize,
  listFrom,
  unwrapResponse,
  useOutreachResource,
} from './outreachUtils';

function campaignKey(campaign) {
  return campaign.campaignKey || campaign.campaign_key || campaign.key;
}

function campaignMode(campaign) {
  return campaign.mode || 'off';
}

function decisionId() {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `outreach-decision-${Date.now()}`;
}

function campaignFailures(campaign, summary) {
  // These checks answer "could this campaign transition shadow -> active
  // right now?" — they are meaningless once the campaign IS active, and
  // rendering them then shows a permanent false "Seven Day Shadow Evidence
  // Required" badge on every live campaign (launch night, 2026-07-28).
  if (campaign.mode === 'active') return [];
  const failures = [
    ...(
    campaign.prerequisiteFailures ||
    campaign.prerequisite_failures ||
    campaign.blockingReasons ||
    []
    ),
    ...(summary?.prerequisites?.prerequisiteFailures || []),
  ];
  if (!campaign.approvalCurrent) failures.push({ code: 'template_approval_required' });
  if (campaign.sourceChanged) failures.push({ code: 'template_source_changed' });
  if (summary?.program?.mode !== 'active') failures.push({ code: 'program_not_active' });
  if (summary?.program?.safeMode !== false) failures.push({ code: 'program_safe_mode' });
  if (summary?.prerequisites?.canSendExternal !== true) {
    failures.push({ code: 'external_sending_prerequisites_failed' });
  }
  const shadowStarted = campaign.shadowStartedAt
    ? new Date(campaign.shadowStartedAt).getTime()
    : 0;
  if (
    campaign.mode !== 'shadow' ||
    !shadowStarted ||
    Date.now() - shadowStarted < 7 * 24 * 60 * 60 * 1000
  ) {
    failures.push({ code: 'seven_day_shadow_evidence_required' });
  }
  const seen = new Set();
  return failures.filter((failure) => {
    const key = typeof failure === 'string' ? failure : failure.code || failure.message;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function OutreachCampaignsPage() {
  const resource = useOutreachResource(async () => {
    const [campaignsResponse, summaryResponse] = await Promise.all([
      founderDashboardAPI.getOutreachCampaigns(),
      founderDashboardAPI.getOutreachSummary(),
    ]);
    const campaignsPayload = unwrapResponse(campaignsResponse);
    return {
      data: {
        items: listFrom(campaignsPayload, 'campaigns', 'items', 'rows'),
        summary: unwrapResponse(summaryResponse),
      },
    };
  }, []);
  const campaigns = listFrom(resource.data, 'campaigns', 'items', 'rows');
  const summary = resource.data?.summary || {};
  const [pendingChange, setPendingChange] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function confirmChange() {
    if (!pendingChange) return;
    setSaving(true);
    setMutationError(null);
    try {
      await founderDashboardAPI.updateOutreachCampaign(pendingChange.key, {
        mode: pendingChange.mode,
        confirmationVersion: pendingChange.confirmationVersion,
        ...(pendingChange.requiresEvidence
          ? {
              evidenceReviewed: pendingChange.evidenceReviewed === true,
              decision: {
                id: pendingChange.decisionId,
                summary:
                  pendingChange.mode === 'active'
                    ? 'Approved first live use after reviewing shadow evidence.'
                    : 'Approved return to shadow after reviewing resolved safety evidence.',
              },
            }
          : {}),
      });
      setSuccess(`${pendingChange.name} is now ${humanize(pendingChange.mode)}.`);
      setPendingChange(null);
      await resource.reload();
    } catch (error) {
      setMutationError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'The campaign was not changed.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Campaigns"
        description="Review server gates and move only approved campaign versions between off, shadow, and active."
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />
      <StaleNotice
        generatedAt={resource.data?.generatedAt || resource.data?.generated_at}
        loadedAt={resource.loadedAt}
      />

      {success ? (
        <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {success}
        </div>
      ) : null}
      {mutationError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {mutationError}
        </div>
      ) : null}

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        empty={campaigns.length === 0}
        emptyTitle="No campaign definitions"
        emptyMessage="Campaigns will appear after the lifecycle registry is installed."
        onRetry={resource.reload}
      >
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const key = campaignKey(campaign);
            const mode = campaignMode(campaign);
            const failures = campaignFailures(campaign, summary);
            const canActivate = failures.length === 0;
            const canEnterShadow = campaign.approvalCurrent === true && !campaign.sourceChanged;
            const confirmationVersion =
              campaign.confirmationVersion ||
              campaign.confirmation_version ||
              campaign.version ||
              campaign.updatedAt ||
              campaign.updated_at;
            const name = campaign.name || humanize(key);

            return (
              <article key={key} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{name}</h4>
                      <StatusBadge value={mode} />
                      <StatusBadge value={campaign.lane || 'lifecycle'} />
                    </div>
                    <p className="mt-1 break-all font-mono text-xs text-gray-500">{key}</p>
                    <p className="mt-2 text-sm text-gray-600">
                      Template {campaign.templateKey || campaign.template_key || '—'} · v
                      {campaign.templateVersion || campaign.template_version || '—'}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2" aria-label={`Change ${name} mode`}>
                    {['off', 'shadow', 'active'].map((nextMode) => {
                      const disabled =
                        nextMode === mode ||
                        (nextMode === 'active' && !canActivate) ||
                        (nextMode === 'shadow' && !canEnterShadow);
                      return (
                        <button
                          key={nextMode}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            setMutationError(null);
                            setPendingChange({
                              key,
                              name,
                              mode: nextMode,
                              confirmationVersion,
                              requiresEvidence:
                                nextMode === 'active' ||
                                (
                                  nextMode === 'shadow' &&
                                  String(campaign.pauseReason || '').startsWith('safety:')
                                ),
                              evidenceReviewed: false,
                              decisionId: decisionId(),
                            });
                          }}
                          className={`min-h-[44px] rounded-lg border px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-45 ${focusRing} ${
                            nextMode === 'active'
                              ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {humanize(nextMode)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 md:grid-cols-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Cohort
                    </p>
                    <p className="mt-1 text-sm text-gray-800">
                      {campaign.cohort || campaign.cohortName || campaign.cohort_name || 'Default'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Daily cap
                    </p>
                    <p className="mt-1 text-sm text-gray-800">
                      {summary.program?.dailyCap || 25}/day
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Automatic pause
                    </p>
                    <p
                      className={`mt-1 text-sm ${
                        campaign.pauseReason ? 'font-medium text-amber-800' : 'text-gray-800'
                      }`}
                    >
                      {campaign.pauseReason
                        ? humanize(campaign.pauseReason)
                        : 'No pause reason reported'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-600">
                  <span>
                    <strong className="text-gray-900">{campaign.counts?.candidates ?? 0}</strong>{' '}
                    candidates
                  </span>
                  <span>
                    <strong className="text-gray-900">{campaign.counts?.sent ?? 0}</strong> sent
                  </span>
                  <span>
                    <strong className="text-gray-900">{campaign.counts?.unknown ?? 0}</strong>{' '}
                    unknown
                  </span>
                </div>

                {campaign.mode === 'active' ? (
                  <p className="mt-4 text-sm text-gray-500">
                    Activated
                    {campaign.activatedAt
                      ? ` ${formatDateTime(campaign.activatedAt)}`
                      : ''}
                  </p>
                ) : failures.length > 0 ? (
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none text-red-600" />
                      <div>
                        <p className="text-sm font-semibold text-red-900">
                          Activation prerequisite failures
                        </p>
                        <div className="mt-2">
                          <ReasonList reasons={failures} />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </PageState>

      {pendingChange ? (
        <Modal
          title={`Set ${pendingChange.name} to ${humanize(pendingChange.mode)}?`}
          description={`The server will verify confirmation version ${
            pendingChange.confirmationVersion || 'unknown'
          } and every current prerequisite before saving.`}
          confirmLabel={`Confirm ${humanize(pendingChange.mode)}`}
          confirmDisabled={
            pendingChange.requiresEvidence &&
            pendingChange.evidenceReviewed !== true
          }
          busy={saving}
          onConfirm={confirmChange}
          onClose={() => !saving && setPendingChange(null)}
        >
          <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
            Campaign mode changes are audited. Active mode can send only when the global switch,
            approved checksum, provider health, cap, and cohort gates all pass.
          </div>
          {pendingChange.requiresEvidence ? (
            <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <input
                type="checkbox"
                checked={pendingChange.evidenceReviewed === true}
                onChange={(event) =>
                  setPendingChange((current) => ({
                    ...current,
                    evidenceReviewed: event.target.checked,
                  }))
                }
                className={`mt-0.5 h-5 w-5 rounded border-amber-400 ${focusRing}`}
              />
              <span>
                I reviewed the current shadow and safety evidence and am making
                this separate, recorded decision.
              </span>
            </label>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
